/**
 * Material-UI Theme Factory
 * Creates MUI theme objects from design tokens
 * All visual properties are driven by the design system
 */

import { createTheme } from '@material-ui/core/styles';
import { colors, darkColors, typography, spacing, borderRadius, shadows, transitions } from './tokens';

/**
 * Create Material-UI theme
 * @param {string} mode - 'light' or 'dark'
 */
export const createMuiTheme = (mode = 'light') => {
  const isDark = mode === 'dark';
  const c = isDark ? darkColors : colors;

  return createTheme({
    // ==========================================
    // PALETTE
    // ==========================================
    palette: {
      type: mode,
      primary: {
        main: c.primary,
        dark: c.primaryHover,
        contrastText: c.primaryContrast,
      },
      secondary: {
        main: c.primary,
        dark: c.primaryHover,
        contrastText: c.primaryContrast,
      },
      error: {
        main: '#EF4444',
        dark: '#DC2626',
        light: '#FCA5A5',
      },
      warning: {
        main: '#F59E0B',
        dark: '#D97706',
        light: '#FCD34D',
      },
      success: {
        main: '#10B981',
        dark: '#059669',
        light: '#34D399',
      },
      info: {
        main: c.primary,
        dark: c.primaryHover,
        light: '#818CF8',
      },
      background: {
        default: c.pageBackground,
        paper: c.cardBackground,
      },
      text: {
        primary: c.textPrimary,
        secondary: c.textSecondary,
        disabled: c.textDisabled,
      },
      divider: c.borderMain,
      action: {
        active: c.textSecondary,
        hover: c.sidebarHoverBg,
        selected: c.sidebarActiveBg,
        disabled: c.textDisabled,
        disabledBackground: c.neutralBg,
        focus: c.sidebarHoverBg,
      },
    },

    // ==========================================
    // TYPOGRAPHY
    // ==========================================
    typography: {
      fontFamily: typography.fontFamily,
      fontSize: 13,
      fontWeightLight: typography.weights.normal,
      fontWeightRegular: typography.weights.normal,
      fontWeightMedium: typography.weights.medium,
      fontWeightBold: typography.weights.bold,
      h1: { fontSize: '28px', fontWeight: typography.weights.bold, lineHeight: 1.2 },
      h2: { fontSize: '24px', fontWeight: typography.weights.bold, lineHeight: 1.25 },
      h3: { fontSize: '20px', fontWeight: typography.weights.semibold, lineHeight: 1.3 },
      h4: { fontSize: '18px', fontWeight: typography.weights.semibold, lineHeight: 1.35 },
      h5: { fontSize: '16px', fontWeight: typography.weights.semibold, lineHeight: 1.4 },
      h6: { fontSize: '14px', fontWeight: typography.weights.semibold, lineHeight: 1.45 },
      subtitle1: { fontSize: '14px', fontWeight: typography.weights.medium, color: c.textSecondary },
      subtitle2: { fontSize: '13px', fontWeight: typography.weights.medium, color: c.textSecondary },
      body1: { fontSize: '14px', fontWeight: typography.weights.normal },
      body2: { fontSize: '13px', fontWeight: typography.weights.normal },
      button: { fontSize: typography.sizes.button, fontWeight: typography.weights.semibold, textTransform: 'none' },
      caption: { fontSize: '12px', color: c.textSecondary },
      overline: { fontSize: '11px', fontWeight: typography.weights.semibold, letterSpacing: '0.08em', textTransform: 'uppercase' },
    },

    // ==========================================
    // SHAPE
    // ==========================================
    shape: {
      borderRadius: parseInt(borderRadius.button),
    },

    // ==========================================
    // SPACING
    // ==========================================
    spacing: 8,

    // ==========================================
    // TRANSITIONS
    // ==========================================
    transitions: {
      easing: {
        easeInOut: transitions.easing,
        easeOut: transitions.easing,
        easeIn: transitions.easing,
        sharp: transitions.easing,
      },
      duration: {
        shortest: parseInt(transitions.fast),
        short: parseInt(transitions.normal),
        standard: parseInt(transitions.normal),
        long: parseInt(transitions.slow),
        enteringScreen: parseInt(transitions.fast),
        leavingScreen: parseInt(transitions.fast),
      },
    },

    // ==========================================
    // SHADOWS
    // ==========================================
    shadows: [
      'none',
      shadows.sm,
      shadows.md,
      shadows.lg,
      shadows.xl,
      shadows.sm,
      shadows.md,
      shadows.lg,
      shadows.xl,
      shadows.sm,
      shadows.md,
      shadows.lg,
      shadows.xl,
      shadows.sm,
      shadows.md,
      shadows.lg,
      shadows.xl,
      shadows.sm,
      shadows.md,
      shadows.lg,
      shadows.xl,
      shadows.sm,
      shadows.md,
      shadows.lg,
      shadows.xl,
    ],

    // ==========================================
    // COMPONENT OVERRIDES
    // ==========================================
    overrides: {

      // ---- MuiButton ----
      MuiButton: {
        root: {
          textTransform: 'none',
          fontWeight: typography.weights.semibold,
          fontSize: typography.sizes.button,
          borderRadius: borderRadius.button,
          padding: '8px 16px',
          lineHeight: 1.4,
          '&:hover': {
            boxShadow: shadows.sm,
          },
        },
        contained: {
          boxShadow: shadows.sm,
          '&:hover': {
            boxShadow: shadows.md,
          },
        },
        containedPrimary: {
          backgroundColor: c.primary,
          '&:hover': {
            backgroundColor: c.primaryHover,
          },
        },
        outlined: {
          borderColor: c.borderMain,
          '&:hover': {
            borderColor: c.primary,
            backgroundColor: c.activeBackground,
          },
        },
        text: {
          '&:hover': {
            backgroundColor: c.activeBackground,
          },
        },
        sizeSmall: {
          padding: '4px 12px',
          fontSize: '12px',
        },
        sizeLarge: {
          padding: '10px 24px',
          fontSize: '14px',
        },
      },

      // ---- MuiIconButton ----
      MuiIconButton: {
        root: {
          padding: 8,
          borderRadius: borderRadius.button,
          '&:hover': {
            backgroundColor: c.sidebarHoverBg,
          },
        },
        sizeSmall: {
          padding: 4,
        },
      },

      // ---- MuiCard ----
      MuiCard: {
        root: {
          borderRadius: borderRadius.card,
          boxShadow: shadows.sm,
          border: `1px solid ${c.borderMain}`,
          backgroundColor: c.cardBackground,
          '&:hover': {
            boxShadow: shadows.md,
          },
        },
      },
      MuiCardHeader: {
        root: {
          padding: '16px 20px',
          borderBottom: `1px solid ${c.borderLight}`,
        },
        title: {
          fontSize: '15px',
          fontWeight: typography.weights.semibold,
          color: c.textPrimary,
        },
        subheader: {
          fontSize: '13px',
          color: c.textSecondary,
        },
      },
      MuiCardContent: {
        root: {
          padding: '16px 20px',
          '&:last-child': {
            paddingBottom: '16px',
          },
        },
      },
      MuiCardActions: {
        root: {
          padding: '12px 20px',
          borderTop: `1px solid ${c.borderLight}`,
        },
      },

      // ---- MuiPaper ----
      MuiPaper: {
        root: {
          backgroundColor: c.cardBackground,
          borderRadius: borderRadius.card,
        },
        elevation1: { boxShadow: shadows.sm },
        elevation2: { boxShadow: shadows.md },
        elevation3: { boxShadow: shadows.lg },
        elevation4: { boxShadow: shadows.xl },
      },

      // ---- MuiTextField / Input ----
      MuiTextField: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: borderRadius.input,
            backgroundColor: c.inputBackground,
            '& fieldset': {
              borderColor: c.inputBorder,
            },
            '&:hover fieldset': {
              borderColor: c.textSecondary,
            },
            '&.Mui-focused fieldset': {
              borderColor: c.inputFocus,
              borderWidth: '1.5px',
            },
            '&.Mui-error fieldset': {
              borderColor: '#EF4444',
            },
          },
        },
      },
      MuiOutlinedInput: {
        root: {
          borderRadius: borderRadius.input,
          backgroundColor: c.inputBackground,
          '& fieldset': {
            borderColor: c.inputBorder,
          },
          '&:hover fieldset': {
            borderColor: c.textSecondary,
          },
          '&.Mui-focused fieldset': {
            borderColor: c.inputFocus,
            borderWidth: '1.5px',
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 3px rgba(79, 70, 229, 0.1)`,
          },
        },
        input: {
          padding: '10px 14px',
          fontSize: '13px',
          color: c.inputText,
          '&::placeholder': {
            color: c.textDisabled,
            opacity: 1,
          },
        },
        notchedOutline: {
          borderColor: c.inputBorder,
        },
      },
      MuiInputBase: {
        root: {
          fontSize: '13px',
          color: c.inputText,
        },
        input: {
          '&::placeholder': {
            color: c.textDisabled,
            opacity: 1,
          },
        },
      },
      MuiInputLabel: {
        root: {
          fontSize: '13px',
          color: c.textSecondary,
          '&.Mui-focused': {
            color: c.inputFocus,
          },
        },
      },
      MuiInput: {
        root: {
          '&:before': {
            borderBottomColor: c.borderMain,
          },
          '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: c.textSecondary,
          },
          '&:after': {
            borderBottomColor: c.inputFocus,
          },
        },
      },
      MuiFilledInput: {
        root: {
          backgroundColor: c.inputBackground,
          borderRadius: `${borderRadius.input} ${borderRadius.input} 0 0`,
          '&:hover': {
            backgroundColor: c.sidebarHoverBg,
          },
          '&:before': {
            borderBottomColor: c.borderMain,
          },
          '&:after': {
            borderBottomColor: c.inputFocus,
          },
        },
      },

      // ---- MuiSelect ----
      MuiSelect: {
        root: {
          fontSize: '13px',
        },
        selectMenu: {
          padding: '10px 14px',
        },
      },
      MuiMenuItem: {
        root: {
          fontSize: '13px',
          padding: '8px 16px',
          borderRadius: borderRadius.button,
          '&:hover': {
            backgroundColor: c.sidebarHoverBg,
          },
          '&.Mui-selected': {
            backgroundColor: c.sidebarActiveBg,
            color: c.activeText,
            '&:hover': {
              backgroundColor: c.sidebarActiveBg,
            },
          },
        },
      },

      // ---- MuiTable ----
      MuiTable: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: 0,
        },
      },
      MuiTableContainer: {
        root: {
          borderRadius: borderRadius.card,
          border: `1px solid ${c.borderMain}`,
          overflow: 'auto',
        },
      },
      MuiTableHead: {
        root: {
          backgroundColor: c.cardBackground,
        },
      },
      MuiTableBody: {
        root: {},
      },
      MuiTableRow: {
        root: {
          '&:hover': {
            backgroundColor: c.sidebarHoverBg,
          },
          '&.Mui-selected': {
            backgroundColor: c.sidebarActiveBg,
          },
        },
        head: {
          '&:hover': {
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTableCell: {
        root: {
          padding: `${spacing.table.cellPaddingY} ${spacing.table.cellPaddingX}`,
          borderBottom: `1px solid ${c.borderMain}`,
          fontSize: '13px',
          color: c.textPrimary,
        },
        head: {
          fontWeight: typography.weights.semibold,
          fontSize: typography.sizes.tableHeader,
          backgroundColor: c.cardBackground,
          color: c.textSecondary,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          borderBottom: `1px solid ${c.borderMain}`,
          padding: `${spacing.table.headerPaddingY || '12px'} ${spacing.table.headerPaddingX || spacing.table.cellPaddingX}`,
        },
        sizeSmall: {
          padding: '8px 16px',
          fontSize: '12px',
        },
      },

      // ---- MuiDialog ----
      MuiDialog: {
        paper: {
          borderRadius: borderRadius.card,
          boxShadow: shadows.lg,
          border: `1px solid ${c.borderMain}`,
        },
      },
      MuiDialogTitle: {
        root: {
          padding: '16px 24px',
          fontSize: '16px',
          fontWeight: typography.weights.semibold,
          color: c.textPrimary,
          borderBottom: `1px solid ${c.borderLight}`,
        },
      },
      MuiDialogContent: {
        root: {
          padding: '20px 24px',
          fontSize: '13px',
          color: c.textPrimary,
        },
      },
      MuiDialogActions: {
        root: {
          padding: '12px 24px',
          borderTop: `1px solid ${c.borderLight}`,
          '& .MuiButton-root': {
            marginLeft: '8px',
          },
        },
      },

      // ---- MuiTooltip ----
      MuiTooltip: {
        tooltip: {
          backgroundColor: isDark ? '#334155' : '#1F2937',
          color: '#FFFFFF',
          fontSize: '12px',
          borderRadius: borderRadius.button,
          padding: '6px 10px',
          boxShadow: shadows.md,
        },
        arrow: {
          color: isDark ? '#334155' : '#1F2937',
        },
      },

      // ---- MuiChip ----
      MuiChip: {
        root: {
          borderRadius: borderRadius.statusPill,
          fontSize: '12px',
          fontWeight: typography.weights.medium,
          height: '28px',
          '& .MuiChip-deleteIcon': {
            color: c.textSecondary,
            '&:hover': {
              color: c.textPrimary,
            },
          },
        },
        colorPrimary: {
          backgroundColor: c.activeBackground,
          color: c.activeText,
        },
        colorSecondary: {
          backgroundColor: c.activeBackground,
          color: c.activeText,
        },
      },

      // ---- MuiCircularProgress ----
      MuiCircularProgress: {
        root: {},
        colorPrimary: {
          color: c.primary,
        },
      },

      // ---- MuiLinearProgress ----
      MuiLinearProgress: {
        root: {
          borderRadius: borderRadius.statusPill,
          height: '6px',
        },
        colorPrimary: {
          backgroundColor: c.neutralBg,
        },
        barColorPrimary: {
          backgroundColor: c.primary,
          borderRadius: borderRadius.statusPill,
        },
      },

      // ---- MuiSnackbar ----
      MuiSnackbar: {
        root: {},
      },
      MuiSnackbarContent: {
        root: {
          borderRadius: borderRadius.card,
          boxShadow: shadows.lg,
          fontSize: '13px',
          backgroundColor: isDark ? '#334155' : '#1F2937',
          color: '#FFFFFF',
        },
      },

      // ---- MuiBackdrop ----
      MuiBackdrop: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      },

      // ---- MuiAutocomplete ----
      MuiAutocomplete: {
        root: {},
        paper: {
          borderRadius: borderRadius.menu,
          border: `1px solid ${c.borderMain}`,
          boxShadow: shadows.lg,
          backgroundColor: c.cardBackground,
        },
        option: {
          fontSize: '13px',
          padding: '8px 14px',
          borderRadius: borderRadius.button,
          '&:hover': {
            backgroundColor: c.sidebarHoverBg,
          },
          '&[aria-selected="true"]': {
            backgroundColor: c.sidebarActiveBg,
            '&:hover': {
              backgroundColor: c.sidebarActiveBg,
            },
          },
        },
        listbox: {
          padding: '6px',
        },
        noOptions: {
          fontSize: '13px',
          color: c.textSecondary,
        },
        tag: {
          borderRadius: borderRadius.statusPill,
          backgroundColor: c.activeBackground,
          color: c.activeText,
          fontSize: '12px',
          height: '24px',
        },
      },

      // ---- MuiCheckbox ----
      MuiCheckbox: {
        root: {
          color: c.textDisabled,
          '&.Mui-checked': {
            color: c.primary,
          },
          '&.Mui-disabled': {
            color: c.textDisabled,
          },
          padding: 6,
        },
        colorPrimary: {
          '&.Mui-checked': {
            color: c.primary,
          },
        },
      },

      // ---- MuiRadio ----
      MuiRadio: {
        root: {
          color: c.textDisabled,
          padding: 6,
        },
        colorPrimary: {
          '&.Mui-checked': {
            color: c.primary,
          },
        },
      },

      // ---- MuiSwitch ----
      MuiSwitch: {
        root: {
          width: 40,
          height: 22,
          padding: 0,
        },
        switchBase: {
          width: 40,
          height: 22,
          padding: 2,
          '&$checked': {
            transform: 'translateX(18px)',
            '& + $track': {
              backgroundColor: c.primary,
              opacity: 1,
            },
          },
        },
        thumb: {
          width: 18,
          height: 18,
          boxShadow: shadows.sm,
        },
        track: {
          borderRadius: 11,
          backgroundColor: c.neutralRing,
          opacity: 1,
        },
        colorPrimary: {
          '&$checked': {
            color: c.primary,
          },
        },
      },

      // ---- MuiFormControl ----
      MuiFormControl: {
        root: {
          margin: '4px 0',
        },
      },
      MuiFormLabel: {
        root: {
          fontSize: '13px',
          color: c.textSecondary,
          '&.Mui-focused': {
            color: c.inputFocus,
          },
        },
      },
      MuiFormHelperText: {
        root: {
          fontSize: '12px',
          marginTop: '4px',
        },
      },

      // ---- MuiTabs ----
      MuiTabs: {
        root: {
          borderBottom: `1px solid ${c.borderMain}`,
          minHeight: '40px',
        },
        indicator: {
          backgroundColor: c.primary,
          height: '3px',
          borderRadius: '3px 3px 0 0',
        },
      },
      MuiTab: {
        root: {
          textTransform: 'none',
          fontWeight: typography.weights.medium,
          fontSize: '13px',
          minWidth: 'auto',
          padding: '8px 16px',
          color: c.textSecondary,
          '&:hover': {
            color: c.textPrimary,
          },
          '&.Mui-selected': {
            color: c.primary,
            fontWeight: typography.weights.semibold,
          },
        },
        textColorInherit: {
          opacity: 1,
          color: c.textSecondary,
          '&.Mui-selected': {
            color: c.primary,
          },
        },
      },

      // ---- MuiList ----
      MuiList: {
        root: {
          padding: '4px',
        },
        padding: '4px',
      },
      MuiListItem: {
        root: {
          borderRadius: borderRadius.button,
          padding: '6px 12px',
          fontSize: '13px',
          '&:hover': {
            backgroundColor: c.sidebarHoverBg,
          },
          '&.Mui-selected': {
            backgroundColor: c.sidebarActiveBg,
            color: c.activeText,
            '&:hover': {
              backgroundColor: c.sidebarActiveBg,
            },
          },
        },
        button: {
          '&:hover': {
            backgroundColor: c.sidebarHoverBg,
          },
        },
      },
      MuiListItemText: {
        primary: {
          fontSize: '13px',
          color: 'inherit',
        },
        secondary: {
          fontSize: '12px',
          color: c.textSecondary,
        },
      },

      // ---- MuiDivider ----
      MuiDivider: {
        root: {
          backgroundColor: c.borderMain,
        },
        light: {
          backgroundColor: c.borderLight,
        },
      },

      // ---- MuiAvatar ----
      MuiAvatar: {
        root: {
          fontSize: '14px',
          fontWeight: typography.weights.semibold,
          backgroundColor: c.activeBackground,
          color: c.activeText,
        },
      },

      // ---- MuiBadge ----
      MuiBadge: {
        badge: {
          fontSize: '11px',
          fontWeight: typography.weights.semibold,
          height: '18px',
          minWidth: '18px',
          padding: '0 5px',
          borderRadius: borderRadius.statusPill,
        },
      },

      // ---- MuiBreadcrumbs ----
      MuiBreadcrumbs: {
        root: {
          fontSize: '13px',
        },
        separator: {
          color: c.textDisabled,
        },
      },

      // ---- MuiDrawer ----
      MuiDrawer: {
        paper: {
          backgroundColor: c.cardBackground,
          borderRight: `1px solid ${c.borderMain}`,
        },
      },

      // ---- MuiAppBar ----
      MuiAppBar: {
        root: {
          boxShadow: 'none',
          borderBottom: `1px solid ${c.borderMain}`,
          backgroundColor: c.cardBackground,
          color: c.textPrimary,
        },
        colorDefault: {
          backgroundColor: c.cardBackground,
          color: c.textPrimary,
        },
      },
      MuiToolbar: {
        root: {
          padding: '0 16px',
        },
      },

      // ---- MuiPopover / MuiPopper ----
      MuiPopover: {
        paper: {
          borderRadius: borderRadius.menu,
          border: `1px solid ${c.borderMain}`,
          boxShadow: shadows.lg,
        },
      },
      MuiPopper: {
        root: {},
      },

      // ---- MuiPagination ----
      MuiPagination: {
        root: {},
      },
      MuiPaginationItem: {
        root: {
          borderRadius: borderRadius.button,
          fontSize: '13px',
          '&.Mui-selected': {
            backgroundColor: c.primary,
            color: c.primaryContrast,
            '&:hover': {
              backgroundColor: c.primaryHover,
            },
          },
        },
      },

      // ---- MuiAccordion ----
      MuiAccordion: {
        root: {
          borderRadius: borderRadius.card,
          border: `1px solid ${c.borderMain}`,
          '&:before': { display: 'none' },
          '&$expanded': { margin: 0 },
        },
      },
      MuiAccordionSummary: {
        root: {
          padding: '12px 16px',
          minHeight: '44px',
          '&$expanded': { minHeight: '44px' },
        },
        content: {
          fontSize: '14px',
          fontWeight: typography.weights.medium,
          '&$expanded': { margin: '8px 0' },
        },
      },
      MuiAccordionDetails: {
        root: {
          padding: '12px 16px',
          borderTop: `1px solid ${c.borderLight}`,
        },
      },

      // ---- MuiAlert (from @material-ui/lab) ----
      MuiAlert: {
        root: {
          borderRadius: borderRadius.card,
          fontSize: '13px',
          padding: '8px 16px',
        },
        standardSuccess: {
          backgroundColor: c.successBg,
          color: c.successText,
          '& .MuiAlert-icon': { color: c.successText },
        },
        standardWarning: {
          backgroundColor: c.warningBg,
          color: c.warningText,
          '& .MuiAlert-icon': { color: c.warningText },
        },
        standardError: {
          backgroundColor: c.errorBg,
          color: c.errorText,
          '& .MuiAlert-icon': { color: c.errorText },
        },
        standardInfo: {
          backgroundColor: c.activeBackground,
          color: c.activeText,
          '& .MuiAlert-icon': { color: c.activeText },
        },
        filledSuccess: {
          backgroundColor: '#10B981',
        },
        filledError: {
          backgroundColor: '#EF4444',
        },
        filledWarning: {
          backgroundColor: '#F59E0B',
        },
        filledInfo: {
          backgroundColor: c.primary,
        },
      },
    },

    // ==========================================
    // PROPS (default props for components)
    // ==========================================
    props: {
      MuiButton: {
        disableElevation: true,
      },
      MuiTextField: {
        variant: 'outlined',
        size: 'small',
      },
      MuiFormControl: {
        variant: 'outlined',
        size: 'small',
      },
      MuiSelect: {
        variant: 'outlined',
      },
      MuiCard: {
        elevation: 1,
      },
      MuiPaper: {
        elevation: 1,
      },
      MuiTooltip: {
        arrow: true,
        enterDelay: 300,
      },
      MuiTable: {
        size: 'small',
      },
    },
  });
};

// Export pre-created themes
export const lightMuiTheme = createMuiTheme('light');
export const darkMuiTheme = createMuiTheme('dark');
